import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ALLIANCE_ROLE_LABELS } from '@kaeliux/shared';
import { useAuthStore } from '../store/authStore';
import { updateDisplayName, uploadProfilePhoto } from '../services/profile.service';
import { getAlliance } from '../services/alliance.service';
import EditTextModal from '../components/EditTextModal';
import PhotoUploadModal from '../components/PhotoUploadModal';
import AllianceLeaderIcon from '../components/AllianceLeaderIcon';
import ProfileAvatar from '../components/ProfileAvatar';
import GuestSignInIdPanel from '../components/auth/GuestSignInIdPanel';
import { RACE_PORTRAITS } from '../utils/raceAssets';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';

export default function ProfilePage() {
    const { profile, session, refreshProfile } = useAuthStore();
    const [editNameOpen, setEditNameOpen] = useState(false);
    const [editPhotoOpen, setEditPhotoOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /**
     * Read live rather than off `profile`: alliance membership changes
     * mid-session via a real gameplay action, so it deliberately isn't part
     * of the merged `UserProfile` (same reasoning as points/premiumTier —
     * see that type's doc comment). Shares the `['alliance', 'mine']` key
     * with the Alliance page, so leaving or joining there refreshes this
     * card too.
     */
    const allianceQuery = useQuery({
        queryKey: ['alliance', 'mine'],
        queryFn: () => getAlliance(),
        staleTime: 30_000,
    });
    const alliance = allianceQuery.data;

    if (!profile) {
        return (
            <main className="max-w-3xl mx-auto p-4">
                <Card>
                    <p className="text-content-muted">Loading profile…</p>
                </Card>
            </main>
        );
    }

    const avatarSrc = profile.photoURL || RACE_PORTRAITS[profile.race ?? 'humans'];

    async function handleSaveName(displayName: string) {
        if (!profile) return;
        setIsSaving(true);
        setError(null);
        try {
            await updateDisplayName(profile.id, displayName);
            await refreshProfile();
            setEditNameOpen(false);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to update nickname.');
        } finally {
            setIsSaving(false);
        }
    }

    async function handleSavePhoto(photo: Blob) {
        if (!profile) return;
        setIsSaving(true);
        setError(null);
        try {
            await uploadProfilePhoto(profile.id, photo);
            await refreshProfile();
            setEditPhotoOpen(false);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to update profile photo.');
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <main className="max-w-3xl mx-auto p-4">
            <Card>
                <h2 className="text-display text-content-primary">Personal</h2>

                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
                    <ProfileAvatar src={avatarSrc} alt={profile.displayName} racePortrait={!profile.photoURL} />
                    <div className="flex flex-col gap-3 items-center sm:items-start">
                        <h3 className="text-heading text-content-primary">{profile.displayName}</h3>
                        <p className="text-content-secondary text-body">{profile.email}</p>
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => setEditPhotoOpen(true)}
                                data-tutorial-id="profile-edit-photo"
                            >
                                Change Photo
                            </Button>
                            <Button
                                type="button"
                                variant="primary"
                                size="sm"
                                onClick={() => setEditNameOpen(true)}
                                data-tutorial-id="profile-edit-name"
                            >
                                Edit Name
                            </Button>
                        </div>
                        {error && <p className="text-status-danger text-body">{error}</p>}
                    </div>
                </div>

                {session?.isGuest && (
                    <div className="pt-4 border-t border-space-700">
                        <GuestSignInIdPanel />
                    </div>
                )}

                <section className="pt-4 border-t border-space-700" data-testid="profile-alliance">
                    <h4 className="text-content-muted text-label font-medium mb-2">Alliance</h4>
                    {allianceQuery.isPending ? (
                        <p className="text-content-muted text-body animate-pulse">Loading alliance…</p>
                    ) : !alliance && session?.isGuest ? (
                        <p className="text-content-muted text-body">
                            You are not in an alliance. Guest accounts can&apos;t join one.
                        </p>
                    ) : !alliance ? (
                        <p className="text-content-muted text-body">
                            You are not in an alliance.{' '}
                            <Link
                                to="/alliance"
                                className="text-content-secondary hover:text-content-primary hover:underline"
                            >
                                Found or join one
                            </Link>
                            .
                        </p>
                    ) : (
                        <p className="flex items-center gap-2 flex-wrap">
                            {alliance.viewerRole === 'leader' && <AllianceLeaderIcon />}
                            <Link
                                to={`/alliance/${alliance.alliance.id}`}
                                className="text-content-primary hover:underline"
                            >
                                <span className="text-content-muted">[{alliance.alliance.tag}]</span>{' '}
                                {alliance.alliance.name}
                            </Link>
                            {alliance.viewerRole && (
                                <span className="text-content-muted text-body">
                                    · {ALLIANCE_ROLE_LABELS[alliance.viewerRole]}
                                </span>
                            )}
                        </p>
                    )}
                </section>
            </Card>

            <EditTextModal
                open={editNameOpen}
                title="Edit nickname"
                label="Nickname"
                initialValue={profile.displayName}
                onSubmit={(value) => { void handleSaveName(value); }}
                onCancel={() => setEditNameOpen(false)}
            />

            <PhotoUploadModal
                open={editPhotoOpen}
                onSubmit={(photo) => { void handleSavePhoto(photo); }}
                onCancel={() => setEditPhotoOpen(false)}
            />

            {isSaving && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 pointer-events-none">
                    <div className="text-content-secondary text-body">Saving…</div>
                </div>
            )}
        </main>
    );
}
